import { createContext, useContext, useEffect, useState } from 'react';
import {
  Color, DISCOVERY_THUMBS_SHOWN, Size, discoveryText, hasThumbnail, photoPositionLine, spotLine, spotTileLabel, t,
  type DiscoveryFileView, type DiscoverySpotView,
} from '@bms2/shared';
import { Button, Textarea } from '../../../components/controls';
import { FormFrame } from '../../../components/forms/FormFrame';
import { Modal } from '../../../components/modals/Modal';
import { FILES_PATH, fileThumbPath, patch, post } from '../api';
import styles from './DiscoverySpot.module.css';

export interface DiscoverySpotProps {
  spot: DiscoverySpotView;
  writes: boolean;
  apiBaseUrl: string;
  onUpload: (spot: DiscoverySpotView) => void;
  /** Writes the point's own note, tagged `template/@spot` (T2771). */
  onNote: (spot: DiscoverySpotView, value: string) => Promise<boolean>;
  onChanged: () => void;
  onError: (message: string) => void;
}

/**
 * 🔒 An upload point's tile (piece 19; T2771, the owner's answer D4a): its tag with its description under it as the
 * hint, how many files it holds, and for a writer the press that opens the upload window with the spot set, reading
 * Add Another Photo once it holds one. A filled photo tile draws a thumbnail per picture, each opening the picture
 * with its kind, note and position, the note editable; a file tile lists its files by name. A note box on the point
 * writes a note tagged to it, as an answer is.
 */
export function DiscoverySpot({ spot, writes, apiBaseUrl, onUpload, onNote, onChanged, onError }: DiscoverySpotProps) {
  const [opened, setOpened] = useState<DiscoveryFileView | null>(null);
  const [all, setAll] = useState(false);
  const [note, setNote] = useState(spot.note ?? '');
  const pictures = usePicturesAfterCard(useContext(CardDrawnContext));
  const photos = spot.kind === 'Photo';
  const shown = all ? spot.files : spot.files.slice(0, DISCOVERY_THUMBS_SHOWN);
  const more = spot.files.length - shown.length;
  const addLabel = spot.files.length === 0 ? spotTileLabel(spot) : t(photos ? 'discovery.addAnotherPhoto' : 'discovery.addAnotherFile');
  // 🔒 Row T2842: a writer takes a photo off the spot through the files' own trash route, so it goes to the Deal's Trash.
  const removePhoto = async (file: DiscoveryFileView) => {
    const outcome = await post(`${FILES_PATH}/${encodeURIComponent(file.id)}/trash`, {}, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setOpened(null);
    onChanged();
  };
  const commitNote = () => {
    if (note.trim() === '' || note.trim() === (spot.note ?? '').trim()) return;
    void onNote(spot, note.trim());
  };
  return (
    <div className={styles.tile} data-discovery-spot={spot.spot} data-discovery-spot-kind={spot.kind}>
      <div className={styles.head}>
        {/* 🔨 The badge is the count, 0 while the point holds nothing, never the round plus: that glyph is the add act's (decision 5160), and the tile's add act is its button. */}
        <span className={styles.glyph} aria-hidden="true" data-discovery-spot-count={spot.spot}>{String(spot.files.length)}</span>
        <span className={styles.words}>
          <span className={styles.tag}>{spot.tag}</span>
          {spot.description ? <span className={styles.hint} data-discovery-spot-hint={spot.spot}>{spot.description}</span> : null}
          <span className={styles.hint}>{spotLine(spot)}</span>
        </span>
        {writes ? (
          <span data-discovery-spot-add={spot.spot}>
            <Button size={Size.Small} color={Color.Inactive} text={[addLabel]} actions={[() => onUpload(spot)]} />
          </span>
        ) : null}
      </div>
      {photos && spot.files.length > 0 ? (
        <ul className={styles.thumbs} data-discovery-thumbs={spot.spot}>
          {shown.map((file) => (
            <li key={file.id} className={styles.thumbCell}>
              <button type="button" className={styles.thumb} onClick={() => setOpened(file)} aria-label={discoveryText('discovery.openPhoto', { name: file.name })} data-discovery-thumb={file.id}>
                {/* 🔒 Decision 6390: the thumbnail, lazily and only once the card has drawn; the button's square holds the place, so nothing jumps. */}
                {pictures && hasThumbnail(file.mimeType) ? <img className={styles.thumbImage} src={`${apiBaseUrl}${fileThumbPath(file.id)}`} alt={file.photoKind ?? file.name} loading="lazy" decoding="async" /> : null}
              </button>
              {writes ? <span data-discovery-thumb-remove={file.id}><RemovePhoto file={file} onRemove={removePhoto} /></span> : null}
            </li>
          ))}
          {more > 0 ? (
            <li className={styles.thumbCell}>
              <button type="button" className={styles.more} onClick={() => setAll(true)} data-discovery-thumbs-more={spot.spot}>{discoveryText('discovery.moreThumbs', { count: more })}</button>
            </li>
          ) : null}
        </ul>
      ) : null}
      {!photos && spot.files.length > 0 ? (
        <ul className={styles.names} data-discovery-file-names={spot.spot}>
          {spot.files.map((file) => <li key={file.id}>{file.name}</li>)}
        </ul>
      ) : null}
      {writes ? (
        <Textarea size={Size.Large} label={t('discovery.pointNote')} value={note} onChange={setNote} onBlur={commitNote} placeholder={t('discovery.pointNotePlaceholder')} rows={2} name={`discovery-note-${spot.spot}`} />
      ) : spot.note ? (
        <span className={styles.readNote} data-discovery-spot-note={spot.spot}>{spot.note}</span>
      ) : null}
      {/* 🔨 Mounted only while open and keyed to the picture, so each opening reads its own note (piece 13). */}
      {opened ? (
        <PhotoWindow
          key={opened.id}
          file={opened}
          writes={writes}
          apiBaseUrl={apiBaseUrl}
          onClose={() => setOpened(null)}
          onSaved={() => { setOpened(null); onChanged(); }}
          onRemove={removePhoto}
          onError={onError}
        />
      ) : null}
    </div>
  );
}

/**
 * 🔒 T2922: the card-wide signal that the card has drawn, set by the Discovery card on the task after its own data
 * drew. A spot mounted outside a card reads null and waits one task of its own.
 */
export const CardDrawnContext = createContext<boolean | null>(null);

/**
 * 🔒 Decision 6390 (row T2917; T2922): the Deal page's own reads come first. A tile's thumbnails enter the tree once
 * the card has drawn, so the page's reads are asked before the first picture is, and the browser's lazy loading
 * holds a tile below the fold until it is scrolled to.
 */
export function useCardDrawn(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(timer);
  }, []);
  return ready;
}

function usePicturesAfterCard(drawn: boolean | null): boolean {
  const own = useCardDrawn();
  return drawn ?? own;
}

/**
 * 🔒 T2771: one picture full size with its kind, its note and its position; a writer edits the note, the file's own
 * description (decision 5440), through the Form frame (decision 3060), and a reader is drawn the close-only window.
 */
function PhotoWindow({ file, writes, apiBaseUrl, onClose, onSaved, onRemove, onError }: { file: DiscoveryFileView; writes: boolean; apiBaseUrl: string; onClose: () => void; onSaved: () => void; onRemove: (file: DiscoveryFileView) => Promise<void>; onError: (message: string) => void }) {
  const [note, setNote] = useState(file.note ?? '');
  const [refusal, setRefusal] = useState<string | null>(null);
  const position = photoPositionLine({ latitude: file.latitude ?? null, longitude: file.longitude ?? null });
  const save = async () => {
    const outcome = await patch(`${FILES_PATH}/${encodeURIComponent(file.id)}`, { description: note.trim() === '' ? null : note.trim() }, apiBaseUrl);
    if (!outcome.ok) {
      setRefusal(outcome.message);
      return onError(outcome.message);
    }
    onSaved();
  };
  const picture = (
    <>
      <img className={styles.viewerImage} src={`${apiBaseUrl}${FILES_PATH}/${file.id}/stream`} alt={file.photoKind ?? file.name} />
      <dl className={styles.facts}>
        <dt>{t('discovery.photoKind')}</dt>
        <dd data-discovery-photo-kind={file.id}>{file.photoKind ?? t('discovery.photoNoKind')}</dd>
        <dt>{t('discovery.photoPosition')}</dt>
        <dd data-discovery-photo-position={file.id}>{position ?? t('discovery.photoNoPosition')}</dd>
      </dl>
    </>
  );
  if (!writes) {
    return (
      <Modal open title={t('discovery.photoTitle')} subtitle={file.name} onCancel={onClose} actions={[{ kind: 'close', label: t('modal.close'), onAct: onClose }]} width="wide" dataModal="discovery-photo">
        <div className={styles.viewer} data-discovery-photo={file.id}>
          {picture}
          <p className={styles.readNote}>{file.note ?? ''}</p>
        </div>
      </Modal>
    );
  }
  return (
    <FormFrame
      host="window"
      open
      title={t('discovery.photoTitle')}
      subtitle={file.name}
      width="wide"
      dataForm="discovery-photo"
      typed={note.trim() !== (file.note ?? '').trim()}
      onCancel={onClose}
      cancelLabel={t('modal.close')}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('discovery.saveNote'), onAct: () => save(), disabled: note.trim() === (file.note ?? '').trim(), disabledReason: t('discovery.noteUnchanged') }}
    >
      <div className={styles.viewer} data-discovery-photo={file.id}>
        {picture}
        <Textarea size={Size.Large} label={t('discovery.photoNote')} value={note} onChange={setNote} rows={3} name={`discovery-photo-note-${file.id}`} />
        <span data-discovery-photo-remove={file.id}><RemovePhoto file={file} onRemove={onRemove} /></span>
      </div>
    </FormFrame>
  );
}

/**
 * 🔒 Row T2842: Remove on a photo, for a writer alone, confirmed first naming the photo. The photo goes to the Deal's
 * Trash through the files' own route, restorable there, and the tile's count and its Add label follow.
 */
function RemovePhoto({ file, onRemove }: { file: DiscoveryFileView; onRemove: (file: DiscoveryFileView) => Promise<void> }) {
  return (
    <Button
      size={Size.Small}
      color={Color.Destructive}
      text={[t('discovery.removePhoto')]}
      actions={[() => void onRemove(file)]}
      confirm={{ title: discoveryText('discovery.removePhotoTitle', { name: file.name }), consequence: { body: t('discovery.removePhotoBody') }, confirmLabel: t('discovery.removePhotoConfirm') }}
    />
  );
}
